import React, { useState, useEffect, useCallback } from 'react';
import {
    Search, Eye, FileText, Calendar, User, Building2,
    Package, CheckCircle, Clock, X,
    ChevronLeft, ChevronRight, AlertCircle,
    ThumbsDown, Users, ShieldCheck, ChevronDown, ChevronUp,
    AlertTriangle, RefreshCw
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { QRCodeSVG } from 'qrcode.react';
export default function MaterialRequestApproval() {
    const [mrList, setMrList] = useState([]);
    const [filteredMRs, setFilteredMRs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [selectedMR, setSelectedMR] = useState(null);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approving, setApproving] = useState(false);



    const [showQRModal, setShowQRModal] = useState(false);
    const [selectedQRItem, setSelectedQRItem] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Current user
    const [currentUser, setCurrentUser] = useState(null);

    // Released By
    const [releasedByInput, setReleasedByInput] = useState('');
    const [releasedByMode, setReleasedByMode] = useState('manual');
    const [allUsers, setAllUsers] = useState([]);
    const [selectedReleasedByUserId, setSelectedReleasedByUserId] = useState('');

    // Approved By (VP-FINANCE/ADMIN)
    const [approvedByUser, setApprovedByUser] = useState(null);

    // Expanded rows
    const [expandedRows, setExpandedRows] = useState(new Set());

    // ✅ Per-MR stock results: { [mrId]: { checking: bool, results: [] } }
    const [mrStockMap, setMrStockMap] = useState({});

    // Modal partial qtys: { [mrDetailId]: number }
    const [partialQtys, setPartialQtys] = useState({});

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentUser(user);
        fetchPendingMRs();
        fetchAllUsers();
    }, []);

    // 1. fetchAllUsers function — tanggal na ang VP detection:
    const fetchAllUsers = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Users`);
            if (res.ok) {
                const users = await res.json();
                setAllUsers(Array.isArray(users) ? users : []);
            }
        } catch (err) { console.error('Error fetching users:', err); }
    };

    useEffect(() => { filterMRs(); }, [searchTerm, departmentFilter, mrList]);

    // ✅ Check stock for a single MR's items — SUMS all matching records per itemCode+warehouseCode
    const checkStockForMR = useCallback(async (mr) => {
        const mrId = mr.id;
        setMrStockMap(prev => ({ ...prev, [mrId]: { checking: true, results: [] } }));

        const results = [];
        for (const item of (mr.mrDetails || [])) {
            const requestedQty = Number(item.requestedQty) || 0;
            let matchingRecords = []; // collect ALL matching inventory rows

            try {
                // ✅ Sum ALL warehouses for same itemCode — no warehouse filter
                const r1 = await fetch(`${API_BASE_URL}api/InventoryData/item/${encodeURIComponent(item.itemCode)}`);
                if (r1.ok) {
                    const arr = await r1.json();
                    matchingRecords = (Array.isArray(arr) ? arr : []).filter(inv => inv.itemCode === item.itemCode);
                }

                // Fallback: search without warehouseCode
                if (matchingRecords.length === 0) {
                    const r2 = await fetch(`${API_BASE_URL}api/InventoryData/search?itemCode=${encodeURIComponent(item.itemCode)}`);
                    if (r2.ok) {
                        const arr = await r2.json();
                        matchingRecords = (Array.isArray(arr) ? arr : []).filter(inv => inv.itemCode === item.itemCode);
                    }
                }

                // Fallback 2: direct by inventoryDataId
                if (matchingRecords.length === 0 && item.inventoryDataId) {
                    const r3 = await fetch(`${API_BASE_URL}api/InventoryData/${item.inventoryDataId}`);
                    if (r3.ok) {
                        const rec = await r3.json();
                        if (rec) matchingRecords = [rec];
                    }
                }
            } catch { /* silent */ }

            // ✅ SUM qty across ALL matching records (handles multiple batches / rows)
            const available = matchingRecords.reduce((sum, inv) => sum + (Number(inv.quantity) || 0), 0);
            const shortage = Math.max(0, requestedQty - available);
            const maxFulfill = Math.min(requestedQty, available);
            const status = matchingRecords.length === 0 ? 'not_found'
                : available <= 0 ? 'out_of_stock'
                    : shortage > 0 ? 'insufficient'
                        : 'ok';

            results.push({
                itemCode: item.itemCode,
                itemDescription: item.itemDescription,
                warehouseCode: item.warehouseCode,
                requestedQty,
                available,
                shortage,
                maxFulfill,
                status,
                inventoryRecords: matchingRecords,
                inventoryRecord: matchingRecords[0] ?? null,
                mrDetailId: item.id ?? `${item.itemCode}-${item.warehouseCode}`,
            });
        }

        setMrStockMap(prev => ({ ...prev, [mrId]: { checking: false, results } }));
        return results;
    }, []);

    const fetchPendingMRs = async () => {
        setLoading(true);
        setMrStockMap({});
        try {
            const res = await fetch(`${API_BASE_URL}api/MRHeader`);
            if (!res.ok) { setMrList([]); setFilteredMRs([]); return; }
            const data = await res.json();
            const mrArray = Array.isArray(data) ? data : (data ? [data] : []);
            const pendingHeaders = mrArray.filter(mr => mr.status === 1);

            const pendingMRsWithDetails = await Promise.all(
                pendingHeaders.map(async (header) => {
                    try {
                        const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${header.id}`);
                        if (detailsRes.ok) {
                            const details = await detailsRes.json();
                            return { ...header, mrDetails: Array.isArray(details) ? details : [] };
                        }
                        return { ...header, mrDetails: [] };
                    } catch { return { ...header, mrDetails: [] }; }
                })
            );

            setMrList(pendingMRsWithDetails);
            setFilteredMRs(pendingMRsWithDetails);

            // ✅ Auto-check stock for ALL MRs after loading
            for (const mr of pendingMRsWithDetails) {
                checkStockForMR(mr); // fire and forget per MR, updates one by one
            }
        } catch {
            setMrList([]); setFilteredMRs([]);
        } finally { setLoading(false); }
    };

    const filterMRs = () => {
        let filtered = [...mrList];
        if (searchTerm) {
            filtered = filtered.filter(mr =>
                mr.mrNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                mr.controlNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                mr.arInvoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                mr.requestedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                mr.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                mr.department?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (departmentFilter !== 'all') {
            filtered = filtered.filter(mr =>
                mr.departmentName === departmentFilter || mr.department === departmentFilter
            );
        }
        setFilteredMRs(filtered);
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getUniqueDepartments = () => {
        const depts = mrList.map(mr => mr.department || mr.departmentName).filter(Boolean);
        return [...new Set(depts)].sort();
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    };

    // ✅ Stock status helpers
    const getStockBadge = (mrId) => {
        const stock = mrStockMap[mrId];
        if (!stock) return null;
        if (stock.checking) return (
            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-xs font-semibold border border-gray-200 animate-pulse">
                <RefreshCw className="w-3 h-3 animate-spin" />Checking...
            </span>
        );
        const results = stock.results || [];
        const hasOut = results.some(r => r.status === 'out_of_stock' || r.status === 'not_found');
        const hasInsufficient = results.some(r => r.status === 'insufficient');
        if (hasOut) return (
            <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold border border-red-200">
                <AlertTriangle className="w-3 h-3" />Out of Stock
            </span>
        );
        if (hasInsufficient) return (
            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold border border-amber-200">
                <AlertTriangle className="w-3 h-3" />Insufficient
            </span>
        );
        if (results.length > 0) return (
            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold border border-emerald-200">
                <CheckCircle className="w-3 h-3" />Stock OK
            </span>
        );
        return null;
    };

    const handleViewForApproval = async (mrId) => {
        try {
            const headerRes = await fetch(`${API_BASE_URL}api/MRHeader/${mrId}`);
            if (!headerRes.ok) {
                Swal.fire({ icon: 'error', title: 'Not Found', text: 'Could not load MR details', confirmButtonColor: '#1e40af' });
                return;
            }
            const headerData = await headerRes.json();
            const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mrId}`);
            let mrDetails = [];
            if (detailsRes.ok) mrDetails = await detailsRes.json();

            const fullMR = { ...headerData, mrDetails: Array.isArray(mrDetails) ? mrDetails : [] };
            setSelectedMR(fullMR);
            const fullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
            setReleasedByInput(fullName);
            setSelectedReleasedByUserId('');
            setReleasedByMode('manual');

            // Pre-fill partialQtys from already-checked stock (or max = requestedQty if not yet checked)
            const existingStock = mrStockMap[mrId];
            const initQtys = {};
            if (existingStock && !existingStock.checking && existingStock.results.length > 0) {
                existingStock.results.forEach(r => { initQtys[r.mrDetailId] = r.requestedQty; });
            } else {
                // Not yet checked — pre-fill with requested qty, will check again
                (fullMR.mrDetails || []).forEach(item => {
                    const key = item.id ?? `${item.itemCode}-${item.warehouseCode}`;
                    initQtys[key] = Number(item.requestedQty) || 0;
                });
                // Recheck for this MR
                checkStockForMR(fullMR).then(results => {
                    const qtys = {};
                    results.forEach(r => { qtys[r.mrDetailId] = r.requestedQty; });
                    setPartialQtys(qtys);
                });
            }
            setPartialQtys(initQtys);
            setShowApprovalModal(true);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load MR details', confirmButtonColor: '#1e40af' });
        }
    };

    // ✅ APPROVE
    // ─── HELPER: Post to Activity Feed ───────────────────────────
    const postActivityFeed = async (mrHeaderId, content, feedType = "text") => {
        try {
            await fetch(`${API_BASE_URL}api/ActivityFeed`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    mrHeaderId,
                    prHeaderId: null,
                    poHeaderId: null,
                    quotationId: null,
                    fuelHeaderId: null,
                    content,
                    feedType,
                    createdBy: currentUser?.userId || 1,
                    createdByName: currentUser?.username || "System", // ← username lang
                }),
            });
        } catch (err) {
            console.warn("⚠️ Activity feed post failed (non-blocking):", err);
        }
    };

    // ─── APPROVE MR ───────────────────────────────────────────────
    const handleApproveMR = async () => {
        if (!selectedMR) return;

        const stockData = mrStockMap[selectedMR.id];
        const stockResults = stockData?.results || [];

        const itemsToFulfill = stockResults.filter(r => {
            const qty = Number(partialQtys[r.mrDetailId] ?? r.requestedQty);
            return qty > 0 && r.status !== 'not_found' && r.status !== 'out_of_stock';
        });

        if (itemsToFulfill.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Nothing to Fulfill',
                text: 'All items are out of stock or not found in inventory.',
                confirmButtonColor: '#dc2626'
            });
            return;
        }

        // 🔥 STEP 0: Pre-validate — block kung lumagpas sa available
        const stockValidation = [];
        for (const r of itemsToFulfill) {
            const requestedNow = Number(partialQtys[r.mrDetailId] ?? r.requestedQty);
            if (requestedNow > r.available) {
                stockValidation.push(`${r.itemCode}: Entered ${requestedNow} but only ${r.available} available!`);
            }
        }

        if (stockValidation.length > 0) {
            await Swal.fire({
                icon: 'error',
                title: '❌ Qty Exceeds Available Stock!',
                html: `<ul class="text-left text-sm list-disc pl-5 mt-2">
        ${stockValidation.map(s => `<li class="text-red-700 font-semibold mb-1">${s}</li>`).join('')}
      </ul>
      <p class="mt-3 text-gray-600 text-sm">Please reduce the Requested Qty to match available stock.</p>`,
                confirmButtonColor: '#dc2626'
            });
            return;
        }

        let releasedByName = '';
        if (releasedByMode === 'manual') {
            releasedByName = releasedByInput.trim();
        } else {
            const pickedUser = allUsers.find(u => u.id === parseInt(selectedReleasedByUserId));
            releasedByName = pickedUser ? `${pickedUser.firstName} ${pickedUser.lastName}` : '';
        }

        const approvedById = String(selectedMR.approvedBy || currentUser?.userId || 1);
        const approvedByName = selectedMR.approvedByName || `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();

        const partialItems = itemsToFulfill.filter(r => {
            const originalQty = selectedMR.mrDetails.find(d =>
                (d.id ?? `${d.itemCode}-${d.warehouseCode}`) === r.mrDetailId
            )?.requestedQty ?? r.requestedQty;
            return Number(partialQtys[r.mrDetailId]) < Number(originalQty);
        });

        const skippedItems = stockResults.filter(r => {
            const qty = Number(partialQtys[r.mrDetailId] ?? r.requestedQty);
            return qty === 0 || r.status === 'not_found' || r.status === 'out_of_stock';
        });

        const hasIssues = partialItems.length > 0 || skippedItems.length > 0;

        const result = await Swal.fire({
            title: 'Confirm Approval',
            html: `
      <div class="text-left text-sm">
        <p class="mb-3 font-semibold text-gray-700">
          Control No: <span class="text-indigo-700 font-mono">${selectedMR.controlNo || selectedMR.mrNo}</span>
        </p>
        <div class="space-y-1 mb-3">
          <p class="text-emerald-700 font-semibold">✅ Items to fulfill: ${itemsToFulfill.length}</p>
          ${partialItems.length > 0
                    ? `<p class="text-amber-600 font-semibold">⚠️ Partial fulfillment: ${partialItems.length} item(s)</p>`
                    : ''}
          ${skippedItems.length > 0
                    ? `<p class="text-red-600 font-semibold">❌ Skipped (no stock): ${skippedItems.length} item(s)</p>`
                    : ''}
        </div>
        ${partialItems.length > 0 ? `
          <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
            <p class="text-xs font-bold text-amber-700 mb-2 uppercase">Partial Items:</p>
            ${partialItems.map(r => `
              <div class="flex justify-between text-xs text-amber-800 mb-1">
                <span class="font-mono font-bold">${r.itemCode}</span>
                <span>Fulfilling <strong>${Number(partialQtys[r.mrDetailId])}</strong> of ${r.requestedQty} requested</span>
              </div>`).join('')}
          </div>` : ''}
        <p class="text-emerald-600 font-semibold mt-2">✓ MR will be submitted for release approval.</p>
      </div>
    `,
            icon: hasIssues ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Approve!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;
        setApproving(true);

        try {
            // 🔥 STEP 0.5: Get GI Number
            let giNumber = '';
            try {
                const giRes = await fetch(`${API_BASE_URL}api/MRHeader/next-gi-number`);
                if (giRes.ok) {
                    const giData = await giRes.json();
                    giNumber = giData.giNumber;
                    console.log(`📋 GI Number: ${giNumber} (reused: ${giData.reused})`);
                }
            } catch {
                giNumber = `GI${String(Date.now()).slice(-6)}`;
                console.warn('⚠️ GI endpoint failed, using fallback:', giNumber);
            }

            // 🔥 STEP 1: Update MR Header status → 2 (For Release)
            const { mrDetails: _details, ...mrHeaderOnly } = selectedMR;
            const updateMRPayload = {
                ...mrHeaderOnly,
                model: mrHeaderOnly.model ?? null,
                status: 2,
                checkBy: String(currentUser?.userId || 1),
                checkByName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || 'Supervisor',
                checkDate: new Date().toISOString(),
                approvedBy: approvedById || String(currentUser?.userId || 1),
                approvedByName: approvedByName || `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim(),
                approvedDate: new Date().toISOString(),
                gI_Number: giNumber,
                ...(releasedByName && { sparepartByApproval: releasedByName, releasedDate: new Date().toISOString() })
            };

            const updateRes = await fetch(`${API_BASE_URL}api/MRHeader/${selectedMR.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateMRPayload)
            });
            if (!updateRes.ok) throw new Error(`Failed to update MR: ${await updateRes.text()}`);

            // 🔥 STEP 2: Log the action
            await fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "APPROVE",
                    module: "Item Request",
                    description: `Approved MR #${selectedMR.controlNo || selectedMR.mrNo}. GI: ${giNumber}. ApprovedBy: ${approvedByName}. Fulfilled: ${itemsToFulfill.length}, Partial: ${partialItems.length}, Skipped: ${skippedItems.length}.`,
                    userId: currentUser?.userId,
                    username: currentUser?.username,
                    fullName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
                }),
            }).catch(() => { });

            // 🔥 STEP 3: Post to Activity Feed
            const feedLines = [
                `✅ MR Approved by ${approvedByName || `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim()}.`,
                `📋 GI Number: ${giNumber}.`,
                `📦 Items: ${itemsToFulfill.length}${partialItems.length > 0 ? `, ${partialItems.length} partial` : ''}${skippedItems.length > 0 ? `, ${skippedItems.length} skipped (no stock)` : ''}.`,
                releasedByName ? `🔧 Sparepart By Approval: ${releasedByName}.` : '',
            ].filter(Boolean).join(' ');

            await postActivityFeed(selectedMR.id, feedLines);

            // 🔥 STEP 4: Success toast
            await Swal.fire({
                icon: 'success',
                title: 'Approved!',
                html: `
        <strong>${selectedMR.controlNo || selectedMR.mrNo}</strong> approved!<br/>
        <span class="text-sm text-gray-600">Status set to For Release.</span><br/>
        <span class="text-sm font-bold text-indigo-700">🗂️ GI Number: ${giNumber}</span>
      `,
                confirmButtonColor: '#10b981',
                timer: 3000,
                showConfirmButton: false
            });

            // 🔥 STEP 5: Reset state
            setShowApprovalModal(false);
            setSelectedMR(null);
            setReleasedByInput(`${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim());
            setSelectedReleasedByUserId('');
            setReleasedByMode('manual');
            setPartialQtys({});
            fetchPendingMRs();

        } catch (error) {
            await fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "APPROVE-FAILED",
                    module: "Material Request",
                    description: `Failed MR #${selectedMR.controlNo || selectedMR.mrNo}. Error: ${error.message}`,
                    userId: currentUser?.userId,
                    username: currentUser?.username,
                    fullName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
                }),
            }).catch(() => { });

            Swal.fire({
                icon: 'error',
                title: 'Approval Failed',
                html: `<pre style="text-align:left;max-height:200px;overflow:auto;font-size:12px">${error.message}</pre>`,
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setApproving(false);
        }
    };

    // ─── REJECT MR ────────────────────────────────────────────────
    const handleRejectMR = async () => {
        if (!selectedMR) return;

        const result = await Swal.fire({
            title: 'Reject Material Request?',
            input: 'textarea',
            inputLabel: 'Reason for Rejection',
            inputPlaceholder: 'Enter reason...',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Reject',
            cancelButtonText: 'Cancel',
            inputValidator: (v) => { if (!v) return 'Please provide a reason!'; }
        });

        if (!result.isConfirmed) return;
        setApproving(true);

        try {
            const { mrDetails: _rd, ...mrRejectOnly } = selectedMR;
            const updateMRPayload = {
                ...mrRejectOnly,
                model: mrRejectOnly.model ?? null,
                status: -1,
                checkBy: String(currentUser?.userId || 1),
                checkByName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || 'Supervisor',
                checkDate: new Date().toISOString(),
                remarks: `${selectedMR.remarks || ''}\n\n[REJECTED] ${result.value}`
            };

            const updateRes = await fetch(`${API_BASE_URL}api/MRHeader/${selectedMR.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateMRPayload)
            });
            if (!updateRes.ok) throw new Error(await updateRes.text());

            // 🔥 Log the action
            await fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "REJECT",
                    module: "Item Request",
                    description: `Rejected MR #${selectedMR.controlNo || selectedMR.mrNo}. Reason: ${result.value}`,
                    userId: currentUser?.userId,
                    username: currentUser?.username,
                    fullName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
                }),
            }).catch(() => { });

            // 🔥 Post to Activity Feed
            await postActivityFeed(
                selectedMR.id,
                `❌ MR Rejected by ${`${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username}. Reason: ${result.value}`
            );

            await Swal.fire({
                icon: 'success',
                title: 'Rejected!',
                text: `${selectedMR.controlNo || selectedMR.mrNo} rejected.`,
                confirmButtonColor: '#dc2626',
                timer: 2000,
                showConfirmButton: false
            });

            setShowApprovalModal(false);
            setSelectedMR(null);
            setPartialQtys({});
            fetchPendingMRs();

        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Rejection Failed',
                text: error.message,
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setApproving(false);
        }
    };

    const totalPages = Math.ceil(filteredMRs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentMRs = filteredMRs.slice(startIndex, startIndex + itemsPerPage);

    const stockStatusStyle = (status) => ({
        ok: 'bg-emerald-50 border-emerald-300 text-emerald-800',
        insufficient: 'bg-amber-50 border-amber-300 text-amber-800',
        out_of_stock: 'bg-red-50 border-red-300 text-red-800',
        not_found: 'bg-gray-50 border-gray-300 text-gray-500',
    }[status] || 'bg-gray-50 border-gray-300 text-gray-500');

    const stockStatusLabel = (status) => ({
        ok: '✅ OK',
        insufficient: '⚠️ Insufficient',
        out_of_stock: '❌ Out of Stock',
        not_found: '❓ Not Found',
    }[status] || '—');

    // Get stock results for the open modal
    const modalStockResults = selectedMR ? (mrStockMap[selectedMR.id]?.results || []) : [];
    const modalStockChecking = selectedMR ? (mrStockMap[selectedMR.id]?.checking ?? false) : false;
    const hasStockIssues = modalStockResults.some(r => r.status !== 'ok');


    const handleOpenQR = async (mrDetails, mr) => {
        if (!mrDetails || mrDetails.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No Items', text: 'No items found in this request.', confirmButtonColor: '#1e40af' });
            return;
        }
        setQrLoading(true);
        try {
            const qrItems = await Promise.all(
                mrDetails.map(async (item) => {
                    try {
                        const res = await fetch(`${API_BASE_URL}api/RackingsInventory/search?itemCode=${item.itemCode}`);
                        if (res.ok) {
                            const data = await res.json();
                            return {
                                itemCode: item.itemCode,
                                itemDescription: item.itemDescription,
                                formatted: data.formatted,
                                rackNo: data.data?.rackNo || '—',
                                qty: item.requestedQty,
                                uom: item.uom,
                            };
                        }
                        return { itemCode: item.itemCode, itemDescription: item.itemDescription, formatted: null, rackNo: '—', qty: item.requestedQty, uom: item.uom };
                    } catch {
                        return { itemCode: item.itemCode, itemDescription: item.itemDescription, formatted: null, rackNo: '—', qty: item.requestedQty, uom: item.uom };
                    }
                })
            );
            setSelectedQRItem({
                controlNo: mr?.controlNo || mr?.mrNo || '—',
                department: mr?.department || mr?.departmentName || 'N/A',
                date: formatDate(mr?.mrDate),
                status: mr?.status,
                requestedBy: mr?.requestedByName || '—',
                qrItems,
                activeQR: qrItems.find(i => i.formatted) || qrItems[0],
            });
            setShowQRModal(true);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load rack info.', confirmButtonColor: '#dc2626' });
        } finally {
            setQrLoading(false);
        }
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 p-6">
            <div className="max-w-8xl mx-auto">

                {/* Header */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 rounded-xl shadow-lg shadow-indigo-200">
                                <CheckCircle className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Item Request Approval</h1>
                                <p className="text-gray-500 mt-1">Review and approve pending material requests</p>
                            </div>
                        </div>
                        <div className="bg-indigo-100 border-2 border-indigo-300 rounded-xl px-6 py-3">
                            <p className="text-xs text-indigo-700 uppercase font-semibold mb-1">Pending Requests</p>
                            <p className="text-4xl font-bold text-indigo-900">{mrList.length}</p>
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input type="text" placeholder="Search by Control No, A/R Invoice, Name, Department..."
                                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-all" />
                        </div>
                        <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}
                            className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none bg-white min-w-[200px] transition-all">
                            <option value="all">All Departments</option>
                            {getUniqueDepartments().map(dept => <option key={dept} value={dept}>{dept}</option>)}
                        </select>
                    </div>
                </div>

                {/* MR Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mb-4"></div>
                                <p className="text-gray-600 font-medium">Loading Pending Requests...</p>
                            </div>
                        </div>
                    ) : filteredMRs.length === 0 ? (
                        <div className="text-center py-20">
                            <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg font-medium">No pending requests</p>
                            <p className="text-gray-400 text-sm mt-2">All material requests have been processed</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                                        <tr>
                                            <th className="px-4 py-4 w-12"></th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Items</th>
                                            {/* ✅ Stock column in the table header */}
                                            <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Stock</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">QR Code</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {currentMRs.map((mr) => (
                                            <React.Fragment key={mr.id}>
                                                <tr className="hover:bg-indigo-50 transition-colors">
                                                    <td className="px-4 py-4 text-center">
                                                        <button onClick={() => toggleRow(mr.id)} className="text-indigo-400 hover:text-indigo-700 transition-colors">
                                                            {expandedRows.has(mr.id) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                        </button>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="w-4 h-4 text-indigo-600" />
                                                            <span className="text-sm font-medium text-gray-900">{formatDate(mr.mrDate)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <User className="w-4 h-4 text-indigo-600" />
                                                            <span className="text-sm font-medium text-gray-900">{mr.requestedByName}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <Building2 className="w-4 h-4 text-indigo-600" />
                                                            <span className="text-sm font-medium text-gray-900">{mr.department || mr.departmentName || 'N/A'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200">
                                                            <FileText className="w-3.5 h-3.5" />{mr.controlNo || mr.mrNo}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200">
                                                            <Package className="w-3.5 h-3.5" />{mr.mrDetails?.length || 0}
                                                        </span>
                                                    </td>
                                                    {/* ✅ Auto stock badge */}
                                                    <td className="px-6 py-4 text-center">
                                                        {getStockBadge(mr.id)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <button
                                                            onClick={() => handleOpenQR(mr.mrDetails, mr)}
                                                            disabled={qrLoading}
                                                            className="flex flex-col items-center gap-1 mx-auto group hover:scale-105 transition-transform disabled:opacity-50"
                                                            title="View QR Code"
                                                        >
                                                            {mr.mrDetails?.[0] ? (
                                                                <div className="bg-white p-1.5 rounded-lg border-2 border-indigo-200 group-hover:border-indigo-500 transition-all shadow-sm">
                                                                    <QRCodeSVG
                                                                        value={`MR-${mr.controlNo || mr.mrNo}`}
                                                                        size={40}
                                                                        bgColor="#ffffff"
                                                                        fgColor="#3730a3"
                                                                        level="M"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div className="w-10 h-10 rounded-lg border-2 border-gray-200 bg-gray-50 flex items-center justify-center">
                                                                    <Package className="w-4 h-4 text-gray-300" />
                                                                </div>
                                                            )}
                                                            <span className="text-xs text-indigo-500 font-semibold group-hover:text-indigo-700">
                                                                {qrLoading ? '...' : 'View QR'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <button onClick={() => handleViewForApproval(mr.id)}
                                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold">
                                                            <Eye className="w-4 h-4" />Review
                                                        </button>
                                                    </td>

                                                </tr>

                                                {/* ✅ Expanded: show per-item stock status inline */}
                                                {expandedRows.has(mr.id) && (
                                                    <tr>
                                                        <td colSpan="8" className="bg-indigo-50 px-8 py-4 border-b border-indigo-100">
                                                            {(mr.mrDetails || []).length === 0 ? (
                                                                <p className="text-sm text-gray-400 italic text-center py-2">No items found.</p>
                                                            ) : (
                                                                <table className="w-full text-sm">
                                                                    <thead>
                                                                        <tr className="text-xs text-indigo-700 uppercase border-b border-indigo-200">
                                                                            <th className="py-2 text-left">Item Code</th>
                                                                            <th className="py-2 text-left">Description</th>
                                                                            <th className="py-2 text-center">Requested</th>
                                                                            <th className="py-2 text-center">Available</th>
                                                                            <th className="py-2 text-center">Shortage</th>
                                                                            <th className="py-2 text-left">UOM</th>
                                                                            <th className="py-2 text-left">Warehouse</th>
                                                                            <th className="py-2 text-left">Purpose</th>
                                                                            <th className="py-2 text-center">Stock Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(mr.mrDetails || []).map((item, idx) => {
                                                                            const stockEntry = (mrStockMap[mr.id]?.results || []).find(
                                                                                r => r.mrDetailId === (item.id ?? `${item.itemCode}-${item.warehouseCode}`)
                                                                            );
                                                                            const isChecking = mrStockMap[mr.id]?.checking;
                                                                            return (
                                                                                <tr key={idx} className="border-b border-indigo-100 last:border-0">
                                                                                    <td className="py-2 font-mono font-bold text-indigo-900">{item.itemCode}</td>
                                                                                    <td className="py-2 text-gray-700">{item.itemDescription}</td>
                                                                                    <td className="py-2 text-center">
                                                                                        <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded font-bold">{item.requestedQty}</span>
                                                                                    </td>
                                                                                    <td className="py-2 text-center">
                                                                                        {isChecking ? (
                                                                                            <span className="text-gray-400 text-xs animate-pulse">...</span>
                                                                                        ) : stockEntry ? (
                                                                                            <span className={`font-bold ${stockEntry.available <= 0 ? 'text-red-600' : stockEntry.available < item.requestedQty ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                                                                {stockEntry.available}
                                                                                            </span>
                                                                                        ) : <span className="text-gray-400">—</span>}
                                                                                    </td>
                                                                                    <td className="py-2 text-center">
                                                                                        {stockEntry && stockEntry.shortage > 0
                                                                                            ? <span className="text-red-600 font-bold text-xs">-{stockEntry.shortage}</span>
                                                                                            : <span className="text-gray-400">—</span>}
                                                                                    </td>
                                                                                    <td className="py-2 text-gray-600">{item.uom}</td>
                                                                                    <td className="py-2 text-gray-600">{item.warehouseCode || '—'}</td>
                                                                                    <td className="py-2 text-gray-600">{item.purpose || '—'}</td>
                                                                                    <td className="py-2 text-center">
                                                                                        {isChecking ? (
                                                                                            <span className="text-xs text-gray-400 animate-pulse">Checking...</span>
                                                                                        ) : stockEntry ? (
                                                                                            <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${stockStatusStyle(stockEntry.status)}`}>
                                                                                                {stockStatusLabel(stockEntry.status)}
                                                                                            </span>
                                                                                        ) : <span className="text-gray-400 text-xs">—</span>}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {filteredMRs.length > 0 && totalPages > 1 && (
                                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                                    <p className="text-sm text-gray-600">
                                        Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                                        <span className="font-bold text-gray-900">{Math.min(startIndex + itemsPerPage, filteredMRs.length)}</span> of{' '}
                                        <span className="font-bold text-gray-900">{filteredMRs.length}</span> requests
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                                            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 hover:bg-gray-100 flex items-center gap-1">
                                            <ChevronLeft className="w-4 h-4" /> Previous
                                        </button>
                                        {[...Array(totalPages)].map((_, idx) => {
                                            const p = idx + 1;
                                            if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1))
                                                return <button key={p} onClick={() => setCurrentPage(p)}
                                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === p ? 'bg-indigo-600 text-white shadow-md' : 'border border-gray-200 hover:bg-gray-100'}`}>{p}</button>;
                                            else if (p === currentPage - 2 || p === currentPage + 2)
                                                return <span key={p} className="px-2 text-gray-400">...</span>;
                                            return null;
                                        })}
                                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                                            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 hover:bg-gray-100 flex items-center gap-1">
                                            Next <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ══════════════════════════════
          APPROVAL MODAL
      ══════════════════════════════ */}
            {showApprovalModal && selectedMR && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[93vh] overflow-hidden flex flex-col animate-slideUp">

                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-5 flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="bg-white/20 p-3 rounded-xl"><FileText className="w-6 h-6" /></div>
                                    <div>
                                        <h3 className="text-xl font-bold">Review Material Request</h3>
                                        <div className="flex items-center gap-3 mt-1.5">
                                            <span className="bg-white/20 px-3 py-1 rounded-lg font-bold font-mono text-sm">{selectedMR.controlNo || selectedMR.mrNo}</span>
                                            <span className="px-3 py-1 rounded-lg text-xs font-bold bg-amber-100 text-amber-700"><Clock className="w-3 h-3 inline mr-1" />Pending Approval</span>
                                            {/* ✅ Stock status in modal header too */}
                                            {!modalStockChecking && hasStockIssues && (
                                                <span className="px-3 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                                                    <AlertTriangle className="w-3 h-3 inline mr-1" />Stock Issue
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button onClick={() => setShowApprovalModal(false)} className="bg-white/10 hover:bg-white/20 rounded-xl p-2.5 transition-all"><X className="w-5 h-5" /></button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="overflow-y-auto flex-1 bg-gray-50 p-6 space-y-5">

                            {/* Request Info */}
                            <div className="bg-white rounded-xl border-2 border-indigo-200 p-5">
                                <h4 className="text-base font-bold text-indigo-900 mb-3 flex items-center gap-2">
                                    <div className="w-1 h-5 bg-indigo-600 rounded-full"></div>Request Information
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Date</p><p className="text-sm font-bold text-gray-900">{formatDate(selectedMR.mrDate)}</p></div>
                                    <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Control No.</p><p className="text-sm font-bold text-indigo-900 font-mono">{selectedMR.controlNo || selectedMR.mrNo}</p></div>
                                    <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">A/R Invoice No.</p><p className="text-sm font-bold text-indigo-900 font-mono">{selectedMR.arInvoiceNo || '—'}</p></div>
                                    <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Name</p><p className="text-sm font-bold text-gray-900">{selectedMR.requestedByName}</p></div>
                                    <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Department</p><p className="text-sm font-bold text-gray-900">{selectedMR.department || selectedMR.departmentName || 'N/A'}</p></div>
                                    {selectedMR.branchSpecify && <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Branch</p><p className="text-sm font-bold text-gray-900">{selectedMR.branchSpecify}</p></div>}
                                </div>
                            </div>

                            {selectedMR.remarks && (
                                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-4">
                                    <p className="text-xs text-amber-700 uppercase font-bold mb-1">Remarks</p>
                                    <p className="text-sm text-gray-800">{selectedMR.remarks}</p>
                                </div>
                            )}

                            {/* ✅ ITEMS + STOCK TABLE IN MODAL (read from already-fetched stock data) */}
                            <div className="bg-white rounded-xl border-2 border-indigo-200 overflow-hidden">
                                <div className="px-5 py-4 border-b border-indigo-100 flex items-center justify-between">
                                    <h4 className="text-base font-bold text-indigo-900 flex items-center gap-2">
                                        <Package className="w-5 h-5 text-indigo-600" />
                                        Items & Stock Status
                                        <span className="text-xs text-gray-400">({selectedMR.mrDetails?.length || 0} items)</span>
                                    </h4>
                                    {hasStockIssues && !modalStockChecking && (
                                        <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-amber-200">
                                            <AlertTriangle className="w-3.5 h-3.5" />Adjust Fulfill Qty below as needed
                                        </span>
                                    )}
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Item Code</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Description</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Warehouse</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">
                                                    Requested Qty
                                                    <span className="block text-gray-400 font-normal normal-case" style={{ fontSize: '10px' }}>editable</span>
                                                </th>                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Available</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Shortage</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Fulfill Qty</th>

                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Status</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">QR</th>                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {(selectedMR.mrDetails || []).map((item, index) => {
                                                const key = item.id ?? `${item.itemCode}-${item.warehouseCode}`;
                                                const stockEntry = modalStockResults.find(r => r.mrDetailId === key);
                                                const fulfillVal = partialQtys[key] ?? (stockEntry?.maxFulfill ?? Number(item.requestedQty));
                                                const isPartial = Number(fulfillVal) < Number(item.requestedQty);
                                                const isZero = Number(fulfillVal) === 0;
                                                const maxAllowed = stockEntry?.maxFulfill ?? Number(item.requestedQty);
                                                const rowBg = !stockEntry ? '' : stockEntry.status === 'insufficient' ? 'bg-amber-50/40' : stockEntry.status !== 'ok' ? 'bg-red-50/40' : '';

                                                return (
                                                    <tr key={item.id || index} className={`${rowBg} hover:bg-gray-50 transition-colors`}>
                                                        <td className="px-4 py-3 font-mono font-bold text-indigo-900 text-xs">{item.itemCode}</td>
                                                        <td className="px-4 py-3 text-gray-700 text-xs max-w-[150px] truncate">{item.itemDescription}</td>
                                                        <td className="px-4 py-3 text-xs text-gray-600">{item.warehouseCode || '—'}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <div className="flex items-center justify-center gap-1.5">
                                                                <input
                                                                    type="number" min={1} max={stockEntry?.available ?? Number(item.requestedQty)}
                                                                    value={partialQtys[item.id ?? `${item.itemCode}-${item.warehouseCode}`] ?? Number(item.requestedQty)}
                                                                    onChange={(e) => {
                                                                        const key = item.id ?? `${item.itemCode}-${item.warehouseCode}`;
                                                                        const maxVal = stockEntry?.available ?? Number(item.requestedQty);
                                                                        const val = Math.min(maxVal, Math.max(1, Number(e.target.value)));
                                                                        setPartialQtys(prev => ({ ...prev, [key]: val }));
                                                                    }}
                                                                    className="w-20 text-center border-2 rounded-lg px-2 py-1 text-sm font-bold focus:outline-none transition-all border-indigo-300 bg-indigo-50 text-indigo-800 focus:border-indigo-500"
                                                                />
                                                            </div>
                                                        </td>                            <td className="px-4 py-3 text-center">
                                                            {modalStockChecking ? (
                                                                <span className="text-gray-400 text-xs animate-pulse">...</span>
                                                            ) : stockEntry ? (
                                                                <span className={`font-bold text-base ${stockEntry.available <= 0 ? 'text-red-600' : stockEntry.available < item.requestedQty ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                                    {stockEntry.available}
                                                                </span>
                                                            ) : <span className="text-gray-400">—</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {stockEntry?.shortage > 0
                                                                ? <span className="text-red-600 font-bold text-xs">-{stockEntry.shortage}</span>
                                                                : <span className="text-gray-400 text-xs">—</span>}
                                                        </td>

                                                        <td className="px-4 py-3 text-center">
                                                            {stockEntry?.status === 'not_found' || stockEntry?.status === 'out_of_stock' ? (
                                                                <span className="text-xs text-gray-400 italic">N/A</span>
                                                            ) : (
                                                                <div className="flex items-center justify-center gap-1.5">
                                                                    {(() => {
                                                                        const key = item.id ?? `${item.itemCode}-${item.warehouseCode}`;
                                                                        const requestedNow = Number(partialQtys[key] ?? item.requestedQty);
                                                                        const fulfill = Math.min(requestedNow, stockEntry?.available ?? requestedNow);
                                                                        const isPartial = fulfill < requestedNow;
                                                                        return (
                                                                            <>
                                                                                <span className={`font-bold text-base px-2 py-0.5 rounded ${isPartial ? 'text-amber-700 bg-amber-50' : 'text-emerald-700 bg-emerald-50'
                                                                                    }`}>
                                                                                    {fulfill}
                                                                                </span>
                                                                                {isPartial && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {modalStockChecking ? (
                                                                <span className="text-xs text-gray-400 animate-pulse">Checking...</span>
                                                            ) : stockEntry ? (
                                                                <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full border ${stockStatusStyle(stockEntry.status)}`}>
                                                                    {stockStatusLabel(stockEntry.status)}
                                                                </span>
                                                            ) : <span className="text-gray-400 text-xs">—</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button
                                                                onClick={() => handleOpenQR([item], selectedMR)}
                                                                className="flex flex-col items-center gap-0.5 mx-auto group hover:scale-105 transition-transform"
                                                                title={`View QR for ${item.itemCode}`}
                                                            >
                                                                <div className="bg-white p-1 rounded-lg border border-indigo-200 group-hover:border-indigo-500 transition-all shadow-sm">
                                                                    <QRCodeSVG
                                                                        value={`MR-${item.itemCode}`}
                                                                        size={32}
                                                                        bgColor="#ffffff"
                                                                        fgColor="#3730a3"
                                                                        level="M"
                                                                    />
                                                                </div>
                                                                <span className="text-xs text-indigo-400 font-semibold group-hover:text-indigo-600" style={{ fontSize: '10px' }}>QR</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {hasStockIssues && !modalStockChecking && (
                                    <div className="mx-5 mb-4 mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-amber-800">
                                            <p className="font-bold mb-1">Stock Issue Detected</p>
                                            <p>Some items don't have enough stock. Adjust the <strong>Fulfill Qty</strong> to release only what's available, or reject and inform the requester.</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Approved By + Released By */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-xl border-2 border-blue-200 p-5">
                                    <h4 className="text-base font-bold text-blue-900 mb-3 flex items-center gap-2">
                                        <div className="w-1 h-5 bg-blue-600 rounded-full"></div>
                                        <ShieldCheck className="w-4 h-4 text-blue-600" />Approved By
                                    </h4>
                                    {selectedMR.approvedByName ? (
                                        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                                            <div className="bg-blue-600 rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0">
                                                <span className="text-white font-bold text-sm">
                                                    {selectedMR.approvedByName.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-blue-900">{selectedMR.approvedByName}</p>
                                                <p className="text-xs text-blue-500">From MR Record</p>
                                            </div>
                                            <CheckCircle className="w-4 h-4 text-blue-500 ml-auto" />
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
                                            <AlertCircle className="w-4 h-4 text-yellow-500" />
                                            <p className="text-sm text-yellow-800 font-semibold">No Approved By on record</p>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-white rounded-xl border-2 border-emerald-200 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-base font-bold text-emerald-900 flex items-center gap-2">
                                            <div className="w-1 h-5 bg-emerald-600 rounded-full"></div>
                                            <Users className="w-4 h-4 text-emerald-600" />Spareparts Approval

                                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Optional</span>
                                        </h4>
                                        <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                                            <button onClick={() => { setReleasedByMode('manual'); setSelectedReleasedByUserId(''); }}
                                                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${releasedByMode === 'manual' ? 'bg-emerald-600 text-white shadow' : 'text-gray-500'}`}>✏️ Manual</button>
                                            <button onClick={() => { setReleasedByMode('dropdown'); setReleasedByInput(''); }}
                                                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${releasedByMode === 'dropdown' ? 'bg-emerald-600 text-white shadow' : 'text-gray-500'}`}>👤 Select</button>
                                        </div>
                                    </div>
                                    {releasedByMode === 'manual' && (
                                        <input type="text" value={releasedByInput} onChange={(e) => setReleasedByInput(e.target.value)}
                                            placeholder="Type name of releasing personnel..."
                                            className="w-full px-4 py-2.5 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:outline-none text-sm font-medium transition-all" />
                                    )}
                                    {releasedByMode === 'dropdown' && (
                                        <select value={selectedReleasedByUserId} onChange={(e) => setSelectedReleasedByUserId(e.target.value)}
                                            className="w-full px-4 py-2.5 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:outline-none bg-white text-sm font-medium transition-all">
                                            <option value="">-- Select user --</option>
                                            {allUsers.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName} {u.authorized ? `(${u.authorized})` : ''}</option>)}
                                        </select>
                                    )}
                                    {(releasedByMode === 'manual' ? releasedByInput.trim() : selectedReleasedByUserId) && (
                                        <div className="mt-2 flex items-center gap-2 text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-200">
                                            <CheckCircle className="w-4 h-4" />
                                            <span className="text-sm font-semibold">
                                                {releasedByMode === 'manual' ? releasedByInput.trim()
                                                    : (() => { const u = allUsers.find(u => u.id === parseInt(selectedReleasedByUserId)); return u ? `${u.firstName} ${u.lastName}` : ''; })()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-between px-8 py-4 border-t border-gray-200 bg-white flex-shrink-0">
                            <button onClick={() => setShowApprovalModal(false)}
                                className="px-5 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-all flex items-center gap-2 text-sm">
                                <X className="w-4 h-4" />Cancel
                            </button>
                            <div className="flex items-center gap-3">
                                <button onClick={handleRejectMR} disabled={approving}
                                    className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all flex items-center gap-2 disabled:opacity-50 text-sm">
                                    {approving ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>Processing...</>
                                        : <><ThumbsDown className="w-4 h-4" />Reject</>}
                                </button>
                                <button onClick={handleApproveMR} disabled={approving || modalStockChecking}
                                    className="px-7 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl font-bold transition-all flex items-center gap-2 text-sm shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed">
                                    {approving ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>Approving...</>
                                        : modalStockChecking ? <><RefreshCw className="w-4 h-4 animate-spin" />Checking Stock...</>
                                            : <><CheckCircle className="w-4 h-4" />Approve & Complete</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* QR CODE MODAL */}
            {showQRModal && selectedQRItem && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-5 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-xl">
                                    <Package className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold">Rack QR Code</h3>
                                    <p className="text-indigo-100 text-xs mt-0.5">{selectedQRItem.controlNo}</p>
                                </div>
                            </div>
                            <button onClick={() => { setShowQRModal(false); setSelectedQRItem(null); }}
                                className="bg-white/10 hover:bg-white/20 rounded-xl p-2 transition-all">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Item Tabs */}
                        {selectedQRItem.qrItems.length > 1 && (
                            <div className="flex gap-1 px-4 pt-4 overflow-x-auto pb-1 flex-wrap">
                                {selectedQRItem.qrItems.map((item, idx) => (
                                    <button key={idx}
                                        onClick={() => setSelectedQRItem(prev => ({ ...prev, activeQR: item }))}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all whitespace-nowrap ${selectedQRItem.activeQR?.itemCode === item.itemCode
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                                            }`}>
                                        {item.itemCode}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* QR Display */}
                        <div className="flex flex-col items-center py-6 px-6 bg-gray-50">
                            {selectedQRItem.activeQR?.formatted ? (
                                <>
                                    <div className="bg-white p-4 rounded-2xl shadow border-2 border-indigo-100 mb-4">
                                        <QRCodeSVG
                                            value={selectedQRItem.activeQR.formatted}
                                            size={180}
                                            bgColor="#ffffff"
                                            fgColor="#3730a3"
                                            level="M"
                                        />
                                    </div>
                                    <span className="font-mono font-bold text-indigo-900 text-base tracking-widest bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-200">
                                        {selectedQRItem.activeQR.formatted}
                                    </span>
                                    <p className="text-xs text-gray-400 mt-2">Scan to identify rack location</p>
                                </>
                            ) : (
                                <div className="flex flex-col items-center py-8 text-gray-400">
                                    <Package className="w-12 h-12 mb-2 opacity-30" />
                                    <p className="text-sm font-medium">No rack assigned</p>
                                    <p className="text-xs">{selectedQRItem.activeQR?.itemCode} not found in inventory</p>
                                </div>
                            )}
                        </div>

                        {/* Item Details */}
                        <div className="px-6 py-4 space-y-2.5">
                            {[
                                { label: 'Item Code', value: selectedQRItem.activeQR?.itemCode || '—', mono: true },
                                { label: 'Description', value: selectedQRItem.activeQR?.itemDescription || '—' },
                                { label: 'Rack No.', value: selectedQRItem.activeQR?.rackNo || '—' },
                                { label: 'Qty', value: `${selectedQRItem.activeQR?.qty || 0} ${selectedQRItem.activeQR?.uom || ''}` },
                                { label: 'Department', value: selectedQRItem.department },
                                { label: 'Requested By', value: selectedQRItem.requestedBy },
                            ].map(({ label, value, mono }) => (
                                <div key={label} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                    <span className="text-xs text-gray-400 font-medium">{label}</span>
                                    <span className={`text-sm text-gray-900 font-semibold ${mono ? 'font-mono text-indigo-800' : ''}`}>{value}</span>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="px-6 pb-6">
                            <button onClick={() => { setShowQRModal(false); setSelectedQRItem(null); }}
                                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-md">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
        </div>
    );
}